import {
  ApolloCache,
  FetchResult,
  Reference,
  useApolloClient,
} from '@apollo/client';
import {
  ArticleMoveMutation,
  ArticleNodeFragment,
  ArticleNodeFragmentDoc,
  Category,
  OptimisticArticleParentDocument,
  OptimisticArticleParentQuery,
  OptimisticArticleParentQueryVariables,
  ParentAddChildMutation,
  useArticleMoveMutation,
  useParentAddChildMutation,
} from 'lib-knowledge-management-common/graphql';
import { ParentRef } from 'lib-knowledge-management-common/models/types';
import { insertIntoArray } from 'lib-knowledge-utils/array';
import { ampli } from 'lib-shared-tracking';

type MutationReturnValue = Promise<
  FetchResult<ArticleMoveMutation> | FetchResult<ParentAddChildMutation>
>;

type MoveFn = (data: {
  articleId: string;
  newCategoryId: string;
  newCollectionId?: string;
  position?: number;
}) => MutationReturnValue;

type UseArticleMoveReturnValue = [
  MoveFn,
  ReturnType<typeof useArticleMoveMutation>[1],
];

function useArticleMove(): UseArticleMoveReturnValue {
  const [moveMutation, mutationResult] = useArticleMoveMutation();
  const [addChildMutation] = useParentAddChildMutation();
  const { cache } = useApolloClient();
  const move: MoveFn = ({
    articleId,
    newCategoryId,
    newCollectionId,
    position,
  }) => {
    const article = cache.readFragment<ArticleNodeFragment>({
      fragment: ArticleNodeFragmentDoc,
      id: cache.identify({
        __typename: 'Article',
        id: articleId,
      }),
    });
    if (!article) throw new Error('No article found');

    const oldParent = article.parent;

    const update = (_cache: ApolloCache<unknown>) => {
      // This will only be run when oldParent is defined.
      if (!oldParent) return;

      // Modify article.parent
      _cache.modify({
        id: _cache.identify(article),
        fields: {
          parent(_, { toReference }) {
            return toReference({
              __typename: 'Category',
              id: newCategoryId,
            }) as Reference;
          },
        },
      });

      // Remove from old parent
      _cache.modify<Category>({
        id: _cache.identify(oldParent),
        fields: {
          articles(existingCategoryRefs = [], { readField }) {
            return existingCategoryRefs.filter(
              (ref) => article.id !== readField('id', ref),
            ) as Reference[];
          },
        },
      });

      // Add to end of new parent at end
      _cache.modify<Category>({
        id: _cache.identify({ __typename: 'Category', id: newCategoryId }),
        fields: {
          articles(existingCategoryRefs = [], { readField, toReference }) {
            // Quick safety check - if the new article is already
            // present in the cache, we don't need to add it again.
            if (
              existingCategoryRefs.some(
                (ref) => readField('id', ref) === article.id,
              )
            ) {
              return existingCategoryRefs;
            }

            // if no order, place at end
            if (typeof position === 'undefined') {
              return [
                ...existingCategoryRefs,
                toReference(article),
              ] as Reference[];
            }

            return insertIntoArray(
              existingCategoryRefs as Reference[],
              position - 1,
              toReference(article)!,
            );
          },
        },
      });

      // Update the ArticleListItems's references
      _cache.modify({
        id: _cache.identify({
          __typename: 'ArticleListItem',
          id: article.id,
        }),
        fields: {
          parentCategories(_, { toReference }) {
            return [
              toReference({
                __typename: 'CategoryListItem',
                id: newCategoryId,
              }),
            ];
          },
          parentCollections(_, { toReference }) {
            return [
              toReference({
                __typename: 'CollectionListItem',
                id: newCollectionId,
              }),
            ];
          },
        },
      });
    };

    if (!oldParent) {
      const parentAddChildMutation = addChildMutation({
        variables: {
          childId: article.id,
          parentId: newCategoryId,
        },
        update,
      });
      return parentAddChildMutation;
    }

    return moveMutation({
      variables: {
        entityId: article.id,
        currentParentId: oldParent.id,
        newParentId: newCategoryId,
        newPosition: position,
      },

      optimisticResponse: createOptimisticResponse({
        cache,
        oldParent,
        article,
        position,
      }),

      update,
    }).then((args) => {
      if (position) {
        ampli.knowledgeArticleReordered({ articleId });
      } else {
        ampli.knowledgeArticleMoved({
          articleId,
          source: 'management',
        });
      }
      return args;
    });
  };

  return [move, mutationResult];
}

export default useArticleMove;

function createOptimisticResponse({
  cache,
  oldParent,
}: {
  article: ArticleNodeFragment;
  oldParent: ParentRef;
  cache: ApolloCache<object>;
  position: number | undefined;
}): ArticleMoveMutation | undefined {
  const optimisticParent = cache.readQuery<
    OptimisticArticleParentQuery,
    OptimisticArticleParentQueryVariables
  >({
    query: OptimisticArticleParentDocument,
    variables: {
      id: oldParent.id,
    },
  })?.category;
  if (!optimisticParent) return undefined;

  return {
    __typename: 'Mutations',
    contentMove: {
      __typename: oldParent.__typename,
      id: oldParent.id,
    },
  };
}
